import React, { useEffect, useState } from "react";
// NAVIGATION
import { useLocation } from "react-router-dom";
// ANT DESIGN COMPONENTS
import { Table, Tag } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
  useLazyGetLiftsQuery,
  useLazyGetLiftsByBankQuery,
  usePostLiftMutation,
  usePutLiftMutation,
} from "../../../redux/lifts/liftAPI";
import { updateOpenDrawerLift, updateLift, updateFormAction } from "../../../redux/lifts/liftSlice";
import { useLazyGetBankQuery } from "../../../redux/banks/bankAPI";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
import { Permission, hasPermission } from "../../../utils/validatePermissions";
// UTILS
import { getTableRowClass, buildFilterString } from "../../../utils/utils";
import { formatUuidT } from "../../../flatbuffers/display";
// COMPONENTS
import GlobalAlert2 from "../../home/GlobalAlert2";
import LiftsTableActions from "./LiftsTableActions";
import FormularyHeaderActionsEdit from "../../components/formularyHeader/FormularyHeaderActionsEdit";
import FormularyHeaderInputText from "../../components/formularyHeader/FormularyHeaderInputText";
import FormularyHeaderInputSelectString from "../../components/formularyHeader/FormularyHeaderInputSelectString";
import FormularyHeaderInputLiftPosition from "../../components/formularyHeader/FormularyHeaderInputLiftPosition";
import FormularyHeaderInputSelectBank from "../../components/formularyHeader/FormularyHeaderInputSelectBank";
// INTERFACES
import { LiftInterface, LiftPositionInterface } from "../../../interfaces/Lift.interface";
import { RootState } from "../../../app/store";
import { FormAction } from "../../../types/enums";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
// CONTSTANTS
import { BLANK_LIFT } from "../../../constants/lifts";

const LiftsTable = () => {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { Column, ColumnGroup } = Table;
  const dispatch = useDispatch();
  const [t] = useTranslation("global");
  const { theme } = useSelector((state: RootState) => state.home);
  const location = useLocation();
  const idBank = new URLSearchParams(location.search).get("idBank");
  const bankType = new URLSearchParams(location.search).get("type");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [lift, setLift] = useState<LiftInterface>({
    ...BLANK_LIFT,
    bankId: (idBank as any) || null,
    type: bankType ? (bankType as unknown as LiftType) : LiftType.ELEVATOR,
  });
  const [lifts, setLifts] = useState<LiftInterface[]>([]);
  const [filterName, setFilterName] = useState("");
  const [filterLiftType, setFilterLiftType] = useState("");
  const [listLiftPosition, setListLiftPosition] = useState<LiftPositionInterface[]>([]);
  const [pagination, setPagination] = useState<any>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
    total: 0,
  });

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const { globalOrganizationId, userLogged } = useSelector((state: RootState) => state.home);
  const {
    liftRefreshTable,
    formAction,
    lift: selectedLift,
  } = useSelector((state: RootState) => state.lift);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetLifts, { data, isLoading, isError, isFetching }] = useLazyGetLiftsQuery();
  const [triggerGetBank, { isLoading: isLoadingGetBank }] = useLazyGetBankQuery();
  const [
    triggerGetLiftsByBank,
    {
      data: dataGetLiftsByBank,
      isLoading: isLoadingGetLiftsByBank,
      isError: isErrorGetLiftsByBank,
      isFetching: isFetchingGetLiftsByBank,
    },
  ] = useLazyGetLiftsByBankQuery();
  const [
    postLift,
    {
      isSuccess: isSuccessPostLift,
      isLoading: isLoadingPostLift,
      isError: isErrorPostLift,
      error: errorPostLift,
      reset: resetPostLift,
    },
  ] = usePostLiftMutation();
  const [
    putLift,
    {
      isSuccess: isSuccesPutLift,
      isError: isErrorPutLift,
      error: errorPutLift,
      isLoading: isLoadingPutLift,
      reset: resetPutLift,
    },
  ] = usePutLiftMutation();

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onClickOpenLiftInformation = (record: LiftInterface) => {
    if (record) {
      dispatch(updateLift(record));
      dispatch(updateOpenDrawerLift(true));
    }
  };

  const getData = async (page: number, limit: number) => {
    let searchAndFilterString = buildFilterString({
      filterName,
      filterLiftType,
    });
    if (
      userLogged &&
      userLogged.organizationId &&
      userLogged.organizationId !== globalOrganizationId
    ) {
      searchAndFilterString = searchAndFilterString.concat(`&org_id=${globalOrganizationId}`);
    }
    const token = await GETJwtToken();
    const BODY: any = {
      page,
      limit,
      token,
      bankId: idBank,
      searchAndFilterString,
    };
    if (idBank && idBank !== "") {
      triggerGetLiftsByBank(BODY);
    } else {
      triggerGetLifts(BODY);
    }
  };

  const handleTableChange = (pagination_: any) => {
    setPagination({
      ...pagination,
      current: pagination_.current,
      pageSize: pagination_.pageSize,
      total: pagination_.total,
    });
    getData(pagination_.current, pagination_.pageSize);
  };

  const refreshTable = () => {
    getData(pagination.current, pagination.pageSize);
  };

  const onChangeFormValue = (newValue: string | number, variableName: string) => {
    const copy: LiftInterface = structuredClone(lift);
    if (variableName === "name" && typeof newValue === "string") {
      copy.name = newValue;
    } else if (variableName === "type") {
      const liftTypeStrToInt: Record<string, LiftType> = {
        [LiftType[LiftType.ELEVATOR]]: LiftType.ELEVATOR,
        [LiftType[LiftType.ESCALATOR]]: LiftType.ESCALATOR,
        [LiftType[LiftType.MOVINGWALK]]: LiftType.MOVINGWALK,
        [LiftType[LiftType.BOT]]: LiftType.BOT,
        [LiftType[LiftType.GPIO]]: LiftType.GPIO,
      };
      copy.type = liftTypeStrToInt[String(newValue)] ?? LiftType.ELEVATOR;
    } else if (variableName === "bankId" && typeof newValue === "string") {
      copy.bankId = newValue as any;
      if (newValue !== "") {
        setListLiftPosition([]);
        copy.position = 0;
      }
    } else if (variableName === "position" && typeof newValue === "number") {
      copy.position = newValue;
    }
    setLift(copy);
  };

  const getBankAndPositions = async (idBankPosition: string | null | undefined) => {
    if (!idBankPosition) return;
    try {
      const token = await GETJwtToken();
      const result = await triggerGetBank({
        token,
        bankId: idBankPosition,
      }).unwrap();
      if (result && result.data) {
        const newArr: LiftPositionInterface[] = [];
        let numberOfLifts = 0;
        if (result.data.numberOfLifts) {
          numberOfLifts = result.data.numberOfLifts;
        }
        // Create new array off lifts position
        for (let x = 0; x < numberOfLifts; x += 1) {
          let reserved = false;
          // Validate if position is reserved
          if (
            result.data.lifts &&
            Array.isArray(result.data.lifts) &&
            result.data.lifts.length > 0
          ) {
            for (let y = 0; y < result.data.lifts.length; y += 1) {
              if (result.data.lifts[y].position === x + 1) {
                reserved = true;
              }
            }
          }
          newArr.push({
            name: `Position No ${x + 1}`,
            id: x + 1,
            reserved,
          });
        }
        setListLiftPosition(newArr);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const checkFormIsValid = () => {
    if (!lift.name || lift.type == null || lift.position === 0) {
      return true;
    }
    return false;
  };

  const onClickResetForm = () => {
    resetPostLift();
    resetPutLift();
    dispatch(updateFormAction(FormAction.Create));
    setLift({
      ...BLANK_LIFT,
      bankId: (idBank as any) || null,
      type: bankType ? (bankType as unknown as LiftType) : LiftType.ELEVATOR,
    });
    if (idBank && idBank !== "") {
      getBankAndPositions(idBank);
    }
  };

  const postChannel = async () => {
    const token = await GETJwtToken();
    const BODY = {
      token,
      body: lift,
    };
    onClickResetForm();
    postLift(BODY);
  };

  const putChannel = async () => {
    const token = await GETJwtToken();
    const BODY = {
      id: lift.id,
      body: lift,
      token,
    };
    onClickResetForm();
    putLift(BODY);
  };

  const onClickSave = async () => {
    if (formAction === "create") {
      postChannel();
    } else if (formAction === "edit") {
      putChannel();
    }
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    const newData = structuredClone(data || dataGetLiftsByBank);
    if (newData && newData.data) {
      setPagination({
        ...pagination,
        total: newData.total,
      });
      setLifts(newData.data);
    }
  }, [data, dataGetLiftsByBank]);

  useEffect(() => {
    if (isError || isErrorGetLiftsByBank) {
      setLifts([]);
    }
  }, [isError, isErrorGetLiftsByBank]);

  useEffect(() => {
    if (globalOrganizationId && globalOrganizationId !== "") {
      getData(pagination.current, pagination.pageSize);
    }

    if (bankType && bankType !== "") {
      const copy: LiftInterface = structuredClone(lift);
      copy.type = bankType as unknown as LiftType;
      setLift(copy);
    }
  }, [globalOrganizationId]);

  useEffect(() => {
    if (liftRefreshTable) {
      getData(pagination.current, pagination.pageSize);
    }
  }, [liftRefreshTable]);

  useEffect(() => {
    if (lift.bankId) {
      getBankAndPositions(String(lift.bankId));
    }
  }, [lift.bankId]);

  useEffect(() => {
    if (isSuccessPostLift || isSuccesPutLift || isErrorPostLift || isErrorPutLift) {
      onClickResetForm();
    }
  }, [isSuccessPostLift, isSuccesPutLift, isErrorPostLift, isErrorPutLift]);

  useEffect(() => {
    if (selectedLift && selectedLift.id && String(selectedLift.id) !== "") {
      setLift({ ...selectedLift });
    }
  }, [selectedLift]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className="mt-3">
      {hasPermission(Permission.LiftsView, permissionSet ?? []) && (
        <Table
          rowClassName={(record, index) => getTableRowClass(index, theme)}
          className="customTable"
          dataSource={lifts}
          loading={
            isLoading ||
            isFetching ||
            isLoadingGetLiftsByBank ||
            isFetchingGetLiftsByBank ||
            isLoadingGetBank ||
            isLoadingPostLift ||
            isLoadingPutLift
          }
          rowKey={(record) => formatUuidT(record.id)}
          size="small"
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 900 }}
          footer={() => (
            <div className="generalStyles__flexEnd">
              <Tag>{pagination.total} Rows</Tag>
            </div>
          )}
        >
          {/** NAME */}
          {FormularyHeaderInputText({
            columnName: t("general.name"),
            inputPlaceholder: t("general.name"),
            formAction,
            value: String(lift.name ?? ""),
            onChangeFormValue,
            variableName: "name",
            itemId: String(lift.id),
            enabledCellClick: true,
            onClickCellName: onClickOpenLiftInformation,
            enabledFilter: true,
            filterValue: filterName,
            setFilterValue: setFilterName,
            refreshTable,
            disableCreateRow: true,
          })}
          {/** TYPE -- */}
          {FormularyHeaderInputSelectString({
            columnName: t("general.type"),
            inputPlaceholder: t("general.type"),
            formAction,
            value:
              (
                [
                  LiftType[LiftType.ELEVATOR],
                  LiftType[LiftType.ESCALATOR],
                  LiftType[LiftType.MOVINGWALK],
                  LiftType[LiftType.BOT],
                  LiftType[LiftType.GPIO],
                ] as string[]
              )[lift.type as unknown as number] ?? String(lift.type),
            onChangeFormValue,
            variableName: "type",
            itemId: String(lift.id),
            values: [
              LiftType[LiftType.ELEVATOR],
              LiftType[LiftType.ESCALATOR],
              LiftType[LiftType.MOVINGWALK],
            ],
            onClickCellName: onClickOpenLiftInformation,
            enabledCellClick: true,
            enabledFilter: true,
            filterValue: filterLiftType,
            setFilterValue: setFilterLiftType,
            refreshTable,
            disableCreateRow: true,
          })}
          {/** BANK -- */}
          {FormularyHeaderInputSelectBank({
            formAction,
            value: lift.bankId ? String(lift.bankId) : "",
            onChangeFormValueText: onChangeFormValue,
            variableName: "bankId",
            itemId: String(lift.id),
            onClickCellName: onClickOpenLiftInformation,
            enabledCellClick: true,
            disableCreateRow: true,
          })}
          {/** LIFT POSITION -- */}
          {FormularyHeaderInputLiftPosition({
            columnName: t("edgeManagement.banks.liftPosition"),
            inputPlaceholder: t("edgeManagement.banks.liftPosition"),
            formAction,
            value: lift.position,
            onChangeFormValue,
            variableName: "position",
            itemId: String(lift.id),
            values: listLiftPosition,
            onClickCellName: onClickOpenLiftInformation,
            enabledCellClick: true,
            bankId: lift.bankId ? String(lift.bankId) : "",
            disableCreateRow: true,
          })}
          {/** ACTIONS */}
          <ColumnGroup align="center" title={t("edgeManagement.banks.actions")}>
            <Column
              align="center"
              title={t("edgeManagement.banks.actions")}
              key="action"
              render={(_: any, record: LiftInterface) => (
                <div>
                  {formAction === "edit" && String(record.id) === String(lift.id) ? (
                    <FormularyHeaderActionsEdit
                      formIsValid={checkFormIsValid()}
                      onClickSave={onClickSave}
                      onClickResetForm={onClickResetForm}
                    />
                  ) : (
                    <LiftsTableActions selectedItem={record} />
                  )}
                </div>
              )}
            />
          </ColumnGroup>
        </Table>
      )}
      {/** ********************************************* */}
      {/** GLOBAL ALERT */}
      <GlobalAlert2
        isError={isErrorPostLift}
        isSuccess={isSuccessPostLift}
        requestType="POST"
        error={errorPostLift}
        name="Lift"
      />
      <GlobalAlert2
        isError={isErrorPutLift}
        isSuccess={isSuccesPutLift}
        requestType="PUT"
        error={errorPutLift}
        name="Lift"
      />
    </div>
  );
};

export default LiftsTable;
